/**
 * On phones the virtual keyboard does not shrink the page (iOS Safari, Chrome Android since 108): it only covers the
 * bottom of it, so the full-screen panel (100dvh) kept its composer and latest messages under the keyboard. While the
 * keyboard is open `--aca-vv-top` / `--aca-vvh` on `root` describe the part of the screen still visible, and the
 * phone layout of the panel fits into it; with the keyboard closed the variables are removed.
 */

/** Less than this between the layout viewport and the visible one is a toolbar, not a keyboard. */
const MIN_KEYBOARD_PX = 120;

/** Keeps the visible-area variables on `root` in step with the virtual keyboard; returns the teardown. */
export function watchKeyboard(win: Window, root: HTMLElement): () => void {
  const vv = win.visualViewport;
  if (!vv) return () => undefined;
  let pending = false;
  let frame = 0;
  const apply = () => {
    pending = false;
    // clientHeight is the layout viewport, which the keyboard leaves alone; a pinch zoom is not a keyboard
    const open = vv.scale < 1.05 && win.document.documentElement.clientHeight - vv.height > MIN_KEYBOARD_PX;
    if (open) {
      root.style.setProperty('--aca-vv-top', `${Math.max(0, Math.round(vv.offsetTop))}px`);
      root.style.setProperty('--aca-vvh', `${Math.round(vv.height)}px`);
    } else {
      root.style.removeProperty('--aca-vv-top');
      root.style.removeProperty('--aca-vvh');
    }
  };
  const schedule = () => {
    if (pending) return;
    pending = true;
    frame = win.requestAnimationFrame(apply);
  };
  vv.addEventListener('resize', schedule);
  vv.addEventListener('scroll', schedule);
  return () => {
    vv.removeEventListener('resize', schedule);
    vv.removeEventListener('scroll', schedule);
    if (pending) win.cancelAnimationFrame(frame);
  };
}
